<header
    class="flex h-72 items-center justify-between px-16 py-12 print:hidden"
    [ngClass]="{
        'lg:grid lg:grid-cols-[1fr_auto_1fr]': showHomeSelector(),
    }"
>
    <div
        class="flex shrink-0 items-center"
        [ngClass]="showHomeSelector() ? 'gap-x-8' : 'gap-x-16'"
    >
        @if (iconMode() === 'menu') {
            <button
                class="hidden md:block"
                data-testid="sidebar-toggle"
                interacta-icon-button
                type="button"
                [icon]="'hamburger'"
                [label]="'SIDEBAR.TOGGLE_SIDEBAR' | translate"
                [size]="'regular'"
                [style]="'text'"
                [tooltipPosition]="'right'"
                (click)="toggleMenu()"
            ></button>
        } @else if (iconMode() === 'back') {
            <nav
                class="flex items-center"
                [attr.aria-label]="'SHARED.BUTTON.GO_BACK' | translate"
            >
                <interacta-back />
            </nav>
        }

        @if (showHomeSelector()) {
            @if (currentHomeId(); as homeId) {
                <interacta-home-selector
                    [currentHomeId]="homeId"
                    [selectableHomes]="homeSelectorItems()"
                    (changeCurrentHome)="changeCurrentHome($event)"
                />
            }
        } @else if (customerLogo) {
            <ng-container
                [ngTemplateOutlet]="customerLogo"
                [ngTemplateOutletContext]="{ logoSrc: customerLogoSrc() }"
            />
        }
    </div>

    @if (isLg() && showHomeSelector() && customerLogo) {
        <ng-container
            [ngTemplateOutlet]="customerLogo"
            [ngTemplateOutletContext]="{ logoSrc: customerLogoSrc() }"
        />
    }

    <div
        class="flex items-center gap-x-20"
        [class.lg:justify-end]="showHomeSelector()"
    >
        @if (showSearchBar()) {
            <interacta-search />
        }

        <div class="flex items-center gap-x-8">
            @if (showAIButton()) {
                <interacta-ai-header-button
                    #aiButton="cdkOverlayOrigin"
                    cdkOverlayOrigin
                    [active]="isAISidePanelOpen()"
                    [describedBy]="
                        isAIDiscoveryCoachMarkOpen()
                            ? coachMark.descriptionId
                            : undefined
                    "
                    [themeMode]="currentTheme().mode"
                    (clicked)="setAIPanelOpen(!isAISidePanelOpen())"
                />

                <interacta-ai-discovery-coach-mark
                    #coachMark
                    class="contents"
                    [isOpen]="isAIDiscoveryCoachMarkOpen()"
                    [origin]="aiButton"
                    [themeMode]="currentTheme().mode"
                    (closing)="closeAIDiscoveryCoachMark($event)"
                    (watchNow)="openAIDiscoveryDialog()"
                />
            }

            <button
                data-testid="useful-links-trigger"
                interacta-icon-button
                type="button"
                [icon]="'apps'"
                [label]="'DIGITAL_WORKPLACE.OPEN_LINKS_DIALOG' | translate"
                [size]="'regular'"
                [style]="'text'"
                (click)="openLinksDialog()"
            ></button>

            @let unreadChats = newUnreadChats();
            <interacta-live-status
                [message]="
                    canShowChat() && unreadChats !== null
                        ? ('CHAT_EMBEDDED.ACCESSIBILITY.UNREAD_CHATS'
                          | translate: { count: unreadChats })
                        : null
                "
            />
            @if (canShowChat()) {
                <button
                    interacta-icon-button
                    type="button"
                    [icon]="hasUnreadChatNotifications() ? 'chat-dot' : 'chat'"
                    [label]="'CHAT_EMBEDDED.OPEN_CHAT' | translate"
                    [ngClass]="
                        hasUnreadChatNotifications()
                            ? 'text-text-primary'
                            : 'text-text'
                    "
                    [size]="'regular'"
                    [style]="'text'"
                    (click)="toggleChat()"
                ></button>
            }

            @if (user(); as user) {
                <button
                    #openMenuBtn="cdkOverlayOrigin"
                    cdkOverlayOrigin
                    data-testid="header-profile-button"
                    type="button"
                    [attr.aria-expanded]="isUserMenuOpen()"
                    [attr.aria-label]="user.fullName"
                    [disabled]="!notPostPrint()"
                    (click)="isUserMenuOpen.set(true)"
                >
                    <interacta-avatar
                        [size]="'regular'"
                        [user]="{
                            id: user.id,
                            fullName: user.fullName,
                            accountPhotoUrl: user.accountPhotoUrl,
                        }"
                    />
                </button>

                <interacta-user-menu
                    class="absolute"
                    [attr.aria-expanded]="isUserMenuOpen()"
                    [availableLanguages]="availableLanguages"
                    [currentLanguage]="currentLanguage()"
                    [currentTheme]="currentTheme()"
                    [currentUser]="user"
                    [isOpen]="isUserMenuOpen()"
                    [origin]="openMenuBtn"
                    (changeLanguage)="changeLanguage.emit($event)"
                    (changeTheme)="changeTheme.emit($event)"
                    (closing)="isUserMenuOpen.set(false)"
                    (logout)="logout.emit()"
                >
                    <ng-container userMenuExtraItems>
                        <button
                            class="w-full"
                            interacta-user-menu-button
                            role="menuitem"
                            type="button"
                            [icon]="'bell'"
                            (click)="openNotificationsSettings.emit()"
                        >
                            {{ 'USER_MENU.NOTIFICATION_SETTINGS' | translate }}
                        </button>
                        <button
                            class="w-full"
                            interacta-user-menu-button
                            role="menuitem"
                            type="button"
                            [icon]="'person'"
                            (click)="openProfile.emit()"
                        >
                            {{ 'USER_MENU.PROFILE' | translate }}
                        </button>
                        <button
                            class="w-full"
                            interacta-user-menu-button
                            role="menuitem"
                            type="button"
                            [icon]="'info'"
                            (click)="openAppInfoAndClose()"
                        >
                            {{ 'USER_MENU.APP_INFO' | translate }}
                        </button>
                    </ng-container>
                </interacta-user-menu>
            }

            @if (notPostPrint()) {
                <interacta-notification-panel-toggle-button />
            }
        </div>
    </div>
</header>

<interacta-ai-discovery-dialog
    [isOpen]="isAIDiscoveryDialogOpen()"
    [themeMode]="currentTheme().mode"
    [videoSrc]="aiDiscoveryVideoSrc()"
    (closing)="closeAIDiscoveryDialog()"
    (openAIPanel)="openAIPanelFromDiscovery()"
/>

<!-- print header (div, not a second banner landmark) -->
<div
    class="mb-24 hidden items-center justify-between border-b border-border-300 bg-text-white px-16 py-12 print:flex"
>
    @if (customerLogoSrc(); as customerLogoSrc) {
        <interacta-image
            [alt]="''"
            [classes]="'h-44 object-contain'"
            [loading]="'eager'"
            [src]="customerLogoSrc.light"
        />
    }
    <interacta-image
        [alt]="'SHARED.PAGE_TITLE.DEFAULT' | translate"
        [classes]="'h-44'"
        [src]="'./assets/images/logo_interacta.png'"
    />
</div>

<!-- customer logo -->
<ng-template #customerLogo let-logoSrc="logoSrc">
    <a
        [attr.aria-label]="'SIDEBAR.HOME' | translate"
        [routerLink]="notPostPrint() ? ['/'] : null"
    >
        <interacta-image
            [alt]="'Customer\'s logo'"
            [classes]="'h-44 object-contain'"
            [fallback]="
                currentTheme().mode === 'dark' ? logoSrc.light : undefined
            "
            [loading]="'eager'"
            [src]="
                currentTheme().mode === 'light' ? logoSrc.light : logoSrc.dark
            "
        />
    </a>
</ng-template>
